import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { useHouseholdBilling, startCheckout } from '../lib/billing';
import { Button } from '../components/ui';
import { LayoutDashboard, User, Inbox, FileText, Users, LogOut, Award, UserCircle2, GraduationCap, CalendarDays, AlertTriangle } from 'lucide-react';
import { Crest } from './Crest';

export function AppLayout() {
  const { user, membership, signOut } = useAuth();
  const { applicants, selectedApplicantId, canSwitch, selectApplicant } = useApplicantContext();
  const { billingStatus, createdBy } = useHouseholdBilling(membership?.householdId);
  const [renewing, setRenewing] = useState(false);
  const nav = useNavigate();

  const isLapsed = billingStatus === 'lapsed';
  const isFounder = !!user && !!createdBy && user.uid === createdBy;

  async function handleRenew() {
    setRenewing(true);
    try {
      await startCheckout();
    } catch {
      setRenewing(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    nav('/signin');
  }

  const navItems = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/scholarships', label: 'Scholarships', icon: Award },
    { to: '/planner', label: 'Planner', icon: CalendarDays },
    { to: '/documents', label: 'Documents', icon: FileText },
    { to: '/schools', label: 'Schools', icon: GraduationCap },
    { to: '/profile', label: 'Profile', icon: User },
    { to: '/household', label: 'Household', icon: Users },
    { to: '/inbox', label: 'Inbox', icon: Inbox },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-paper border-b border-line sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 font-serif font-semibold text-ink">
              <Crest size={26} />
              HomeScholar Pro
            </Link>
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${
                      isActive ? 'bg-forest-50 text-forest-800' : 'text-soft hover:bg-cream'
                    }`
                  }
                >
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {/* Applicant switcher — shown only when there are 2+ applicants */}
              {applicants.length > 1 && (
                <div className="hidden sm:flex items-center gap-1.5 pl-2 border-l border-line">
                  <UserCircle2 size={14} className="text-soft" />
                  {canSwitch ? (
                    <select
                      value={selectedApplicantId ?? ''}
                      onChange={(e) => selectApplicant(e.target.value)}
                      className="text-xs bg-transparent border-0 text-soft font-medium focus:outline-none focus:ring-1 focus:ring-forest-600 rounded cursor-pointer pr-1"
                      title="Switch active applicant"
                    >
                      {applicants.map((a) => (
                        <option key={a.applicant_id} value={a.applicant_id}>
                          {a.display_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-soft font-medium">
                      {applicants.find((a) => a.applicant_id === selectedApplicantId)?.display_name ?? ''}
                    </span>
                  )}
                </div>
              )}
              <span className="hidden md:inline text-xs text-soft">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut size={14} className="mr-1" />
                Sign out
              </Button>
            </div>
          </div>
          {/* Mobile applicant switcher (visible below header on small screens) */}
          {applicants.length > 1 && (
            <div className="sm:hidden flex items-center gap-1.5 pb-2 border-b border-line">
              <UserCircle2 size={12} className="text-soft" />
              {canSwitch ? (
                <select
                  value={selectedApplicantId ?? ''}
                  onChange={(e) => selectApplicant(e.target.value)}
                  className="text-xs bg-transparent border-0 text-soft font-medium focus:outline-none flex-1"
                  title="Switch active applicant"
                >
                  {applicants.map((a) => (
                    <option key={a.applicant_id} value={a.applicant_id}>
                      {a.display_name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-xs text-soft font-medium">
                  {applicants.find((a) => a.applicant_id === selectedApplicantId)?.display_name ?? ''}
                </span>
              )}
            </div>
          )}
          <nav className="md:hidden flex items-center gap-1 pb-2 overflow-x-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-1 px-2.5 py-1 text-xs rounded-md whitespace-nowrap ${
                    isActive ? 'bg-forest-50 text-forest-800' : 'text-soft'
                  }`
                }
              >
                <item.icon size={14} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* Subscription-lapsed banner (read-only mode). Reads/printing still work;
          editing is blocked by the security rules until the founder renews. */}
      {isLapsed && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="mx-auto max-w-7xl px-4 py-2.5 flex items-center gap-3 text-sm text-amber-900">
            <AlertTriangle size={16} className="shrink-0" />
            <span className="flex-1">
              Your household subscription has lapsed, so it's <strong>read-only</strong> for now.
              Your data is safe — {isFounder ? 'renew to start editing again.' : 'ask the account owner to renew.'}
            </span>
            {isFounder && (
              <Button size="sm" onClick={handleRenew} disabled={renewing}>
                {renewing ? 'Opening…' : 'Renew subscription'}
              </Button>
            )}
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-6">
        {!membership ? (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
            We couldn't find a household for this account yet. If you just signed up, please refresh the page in a moment.
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
